import { COLOR } from '@/utils/color';
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  ExclamationCircleOutlined,
  LoadingOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Divider, Row, Tag, Tooltip, Col, Space, Button } from 'antd';
import React, { useEffect } from 'react';
import styles from './CoverageStatusConnections.module.css';
import { connect } from 'dva';
import { Icon } from '@ant-design/compatible';
import Label from '@/components/Label/Label';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { converageConnections } from '@/utils/constant';
import { decryptAES, formatDateAndTime, formatRoleNames } from '@/utils/utils';

const isLastIndex = (index, array) => {
  return index === array.length - 1;
};

const getStatusIcon = (status, isLoading, isDisabled) => {
  if (isDisabled)
    return (
      <Icon
        type="stop"
        style={{
          fontSize: '1rem',
          color: COLOR.DISABLED,
        }}
      />
    );

  if (isLoading) return <LoadingOutlined style={{ fontSize: '1rem' }} />;
  if (status === 200)
    return <CheckCircleTwoTone style={{ fontSize: '1rem' }} twoToneColor={COLOR.GREEN} />;
  if (status === 500)
    return <CloseCircleTwoTone style={{ fontSize: '1rem' }} twoToneColor={COLOR.RED} />;

  return <ExclamationCircleOutlined style={{ fontSize: '1rem', color: COLOR.YELLOW }} />;
};

const modifyConnectionTypes = (
  converageConnections,
  availableConnections,
  currentConnectionMode
) => {
  // if testing data is not available then return the basic connections
  if (isEmpty(availableConnections)) {
    // if aws & gcp account then we need to show all dummy but the advanced conenction should be disabled
    if (currentConnectionMode === 'basic') {
      const dummyBasicConnections = converageConnections.map(connection => {
        if (connection.mode === 'advanced') {
          return {
            ...connection,
            status: 500,
            loading: false,
            description:
              'This feature is only available for cloud accounts with an Advanced protection level.',
            isDisabled: true,
          };
        } else return connection;
      });
      return dummyBasicConnections;
    }
    return converageConnections.filter(connection => connection.mode === 'basic');
  }

  const modifiedConnectionTypes = [];
  const currentConnections = {};
  if (currentConnectionMode === 'basic') {
    const basicKeys = converageConnections
      .filter(connection => connection.mode === 'basic')
      .map(connection => connection.key);

    Object.keys(availableConnections).map(connection => {
      if (basicKeys.includes(connection)) {
        currentConnections[connection] = availableConnections[connection];
      }
    });
  }

  for (const connection of converageConnections) {
    const currentMappingConnections =
      currentConnectionMode === 'basic' ? currentConnections : availableConnections;

    // if currentMappingConnections is empty, then it means that the connection is not available yet
    if (!isEmpty(currentMappingConnections)) {
      const connectionAvailable = Object.keys(currentMappingConnections).find(
        availableConnection => availableConnection === connection.key
      );
      if (connectionAvailable) {
        modifiedConnectionTypes.push({
          ...connection,
          status: availableConnections[connectionAvailable].status,
          loading: availableConnections[connectionAvailable].loading,
          message: availableConnections[connectionAvailable].message,
        });
      } else {
        modifiedConnectionTypes.push({
          ...connection,
          loading: false,
          description:
            'This feature is only available for cloud accounts with an Advanced protection level.',
          isDisabled: true,
        });
      }
    }
  }
  return modifiedConnectionTypes;
};

const CoverageStatusConnections = ({
  testConnectionTypes,
  byPassAndTestAll = false,
  isEditCloudAccount = false,
  cxrRegion,
  cloudAccountData,
  connectionProtectionType = 'basic',
  roleNames = {},
  ...props
}) => {
  const [currentConnectionMode, setCurrentConnectionMode] = React.useState();

  const isAWSAccount = cloudAccountData.provider.toLowerCase() === 'aws';
  const isGCPAccount = cloudAccountData.provider.toLowerCase() === 'gcp';
  const isAzureAccount = cloudAccountData.provider.toLowerCase() === 'azure';
  const testCloudConnectionApis = connectionMode => {
    try {
      const { dispatch } = props;
      const defaultRegion = cloudAccountData?.cloudXrayConfig?.cxrRegion || 'us-east-1';

      // Create the payload for AWS
      const payloadForTestConnectionAWS = {
        cloudAccountId: cloudAccountData.id,
        roleArn: cloudAccountData.roleArn,
        testApisOnly: false,
        protectionLevel: connectionMode,
        externalId: cloudAccountData.externalId,
        cxrRegion: defaultRegion,
        authKey: cloudAccountData.cloudXrayConfig?.authKey,
        isEditCloudAccount,
        provider: cloudAccountData.provider.toLowerCase(),
      };

      // Payload for GCP
      let payloadForTestConnectionGCP = null;
      if (isGCPAccount) {
        payloadForTestConnectionGCP = {
          cloudAccountId: cloudAccountData.id,
          testApisOnly: false,
          protectionLevel: connectionMode,
          externalId: cloudAccountData.externalId,
          authKey: cloudAccountData.cloudXrayConfig?.authKey,
          isEditCloudAccount,
          serviceAccount: isEditCloudAccount
            ? cloudAccountData.userkeys
            : decryptAES(cloudAccountData.userkeys),
          provider: cloudAccountData.provider.toLowerCase(),
          roleNames: Object.keys(roleNames).length
            ? formatRoleNames(roleNames)
            : Object.keys(cloudAccountData.roleNames).length
            ? cloudAccountData.roleNames
            : {},
        };
      }

      // Payload for Azure
      let payloadForTestConnectionAzure = null;
      if (isAzureAccount) {
        payloadForTestConnectionAzure = {
          cloudAccountId: cloudAccountData.id,
          testApisOnly: false,
          protectionLevel: connectionMode,
          isEditCloudAccount,
          provider: cloudAccountData.provider.toLowerCase(),
          credentials: isEditCloudAccount
            ? cloudAccountData.userkeys
            : decryptAES(cloudAccountData.userkeys),
          externalId: cloudAccountData.externalId,
        };
      }

      // Determine the correct payload based on the account type
      const payloadToSend = isGCPAccount
        ? payloadForTestConnectionGCP
        : isAzureAccount
        ? payloadForTestConnectionAzure
        : payloadForTestConnectionAWS;

      dispatch({
        type: 'cloudAccount/testApis',
        payload: payloadToSend,
      });
    } catch (err) {
      console.error('Error in testCloudConnectionApis: ', err.message);
    }
  };

  const getLastHealthCheck = (lastHealthCheck, lastScan) => {
    // if (isAzureAccount) return formatDateAndTime(lastScan?.toDate());

    if (!isEmpty(lastHealthCheck)) return formatDateAndTime(lastHealthCheck?.toDate());

    return formatDateAndTime(lastScan?.toDate());
  };

  useEffect(() => {
    // * CDM on byPassAndTestAll
    if (byPassAndTestAll && connectionProtectionType) {
      setCurrentConnectionMode(connectionProtectionType);
      testCloudConnectionApis(connectionProtectionType);
    } else {
      // if protection level is not available in cloudAccount then we will take basic for default
      setCurrentConnectionMode(cloudAccountData.protectionLevel || 'basic');
    }
  }, [byPassAndTestAll]);

  return (
    <div
      style={{
        marginTop: 14,
        marginBottom: 14,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 10,
        }}
      >
        <h6
          style={{
            fontSize: '1.2rem',
            fontWeight: 500,
          }}
        >
          Connection Status
        </h6>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Tooltip
            title={
              getLastHealthCheck(cloudAccountData.lastHealthCheck, cloudAccountData.lastScan)
                .exactTime
            }
          >
            <span
              style={{
                color: COLOR.DISABLED,
                fontSize: '0.8rem',
              }}
            >
              Last checked &nbsp;
              {
                getLastHealthCheck(cloudAccountData.lastHealthCheck, cloudAccountData.lastScan)
                  .relativeTime
              }
            </span>
          </Tooltip>

          <Tooltip title="Refresh">
            <ReloadOutlined
              onClick={() => testCloudConnectionApis(currentConnectionMode)}
              style={{
                fontSize: '1rem',
                marginLeft: 8,
                cursor: 'pointer',
                color: COLOR.BLUE,
              }}
            />
          </Tooltip>
        </div>
      </div>

      <div
        style={{
          background: COLOR.GREY,
          borderRadius: 8,
        }}
      >
        <div
          style={{
            padding: 16,
            paddingBottom: 0,
            borderLeft: `4px solid ${COLOR.BLUE}`,
          }}
        >
          <div>
            {modifyConnectionTypes(
              converageConnections,
              testConnectionTypes,
              currentConnectionMode
            ).map((connection, index) => {
              const shouldRender =
                ((isGCPAccount || isAzureAccount) && connection.key !== 'eagleEye') || isAWSAccount;

              if (shouldRender) {
                return (
                  <>
                    <Row
                      style={{
                        padding: '6px 0',
                      }}
                    >
                      <Col span={18}>
                        <div
                          style={{
                            padding: '0 5px',
                            display: 'flex',
                            marginTop: 10,
                            marginBottom: 10,
                          }}
                        >
                          <div>
                            {getStatusIcon(
                              connection.status,
                              connection.loading,
                              connection.isDisabled
                            )}
                          </div>

                          <div
                            style={{
                              margin: 0,
                              marginLeft: 12,
                            }}
                          >
                            {connection.isDisabled ? (
                              <>
                                <p className={styles['disabled_text']}>{connection.name}</p>
                                <p className={styles['caption_div']}>{connection.description}</p>
                              </>
                            ) : (
                              <>
                                <p className={styles['status_text']}>{connection.name}</p>
                              </>
                            )}
                          </div>
                        </div>
                      </Col>
                      {!connection.loading && !connection.isDisabled && connection.status !== 200 && (
                        <Col
                          span={6}
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                          }}
                        >
                          <Tooltip title={connection.message}>
                            <Tag color="red"> View Error</Tag>
                          </Tooltip>
                        </Col>
                      )}
                    </Row>
                    {!isLastIndex(index, converageConnections) && (
                      <Divider
                        dashed
                        style={{
                          margin: 0,
                        }}
                      />
                    )}
                  </>
                );
              }
              return null;
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default connect(({ cloudAccount }) => ({
  testConnectionTypes: cloudAccount.testConnectionTypes,
}))(CoverageStatusConnections);
